// The vault's copy on the user's own disk.
//
// Browser storage is the wrong place for anything that must survive the user
// clearing site data - localStorage, IndexedDB and the Cache API all go, and no
// API opts out. A real file does not: "Clear browsing data" has no business
// touching the user's documents.
//
// TWO TRANSPORTS, chosen at runtime
//
//   handle  - File System Access. The user picks a file once; we keep a
//             FileSystemFileHandle and can re-write it in place afterwards. The
//             HANDLE lives in IndexedDB and so dies with a wipe, but the FILE
//             does not - reconnecting is one "Open vault file" click, not a
//             re-pairing. Chromium desktop today.
//
//   picker  - <a download> out, <input type="file"> back in. No handle, so the
//             user re-saves on every export, but the file is just as durable.
//
// Which transport a device gets is decided by FEATURE DETECTION, never by
// sniffing the platform - the matrix moves, and a wrong guess here silently
// costs a user their vault. On the phones this app targets the picker transport
// is the expected one today (iOS has no File System Access API at all; Android
// Chromium has not shipped the pickers), but nothing below depends on that
// being true: if a browser gains them, it gets handles automatically.
//
// The distinction matters less than it looks. A wipe destroys the handle too,
// so RECOVERY is identical either way - the user points at the file once. What
// handles buy is silent re-writes during normal use; without them each save is
// a deliberate download or share.
//
// WHAT IS IN THE FILE
//
// Ciphertext only. The vault's PRF mode wraps with a key derived from the
// passkey's PRF output, and that key never leaves the authenticator, so the
// file is inert on its own: restoring it still needs the passkey and a
// biometric. No key material is ever written here.

const DB_NAME = "tide-vault-file";
const DB_VERSION = 1;
const STORE = "handles";
const HANDLE_KEY = "vaultFile";

/** File System Access available? Decides transport; never assume by platform. */
export function supportsFileHandles() {
  return typeof window !== "undefined"
    && typeof window.showSaveFilePicker === "function"
    && typeof window.showOpenFilePicker === "function";
}

/* ---------------- handle persistence ---------------- */

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idb(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

/**
 * The handle from a previous session, if the browser still has it.
 *
 * Handles survive a normal reload but not a storage wipe - which is the case
 * this whole module exists for, so a null here is expected and means "ask the
 * user to point at the file again", not "the file is gone".
 */
export async function storedHandle() {
  try {
    return (await idb("readonly", (s) => s.get(HANDLE_KEY))) || null;
  } catch {
    return null;
  }
}

async function rememberHandle(handle) {
  // Handles are structured-cloneable, which is the only reason this works.
  try { await idb("readwrite", (s) => s.put(handle, HANDLE_KEY)); } catch { }
}

export async function forgetHandle() {
  try { await idb("readwrite", (s) => s.delete(HANDLE_KEY)); } catch { }
}

/* ---------------- permissions ---------------- */

/**
 * Permission on a handle is not permanent. It has to be re-granted after a
 * restart, and re-granting requires a user gesture - so this is only ever
 * called from a click, never from boot.
 */
async function ensurePermission(handle, mode = "readwrite") {
  if (!handle?.queryPermission) return true;
  if ((await handle.queryPermission({ mode })) === "granted") return true;
  return (await handle.requestPermission({ mode })) === "granted";
}

/**
 * What the file connection can do right now, without prompting anything.
 *
 * The distinction matters because a lapsed permission and a missing file look
 * identical to a naive save - both just fail - and they need opposite
 * responses: one is a single "reconnect" tap, the other is first-time setup.
 *
 * @returns {Promise<"unsupported"|"not-connected"|"needs-permission"|"ready">}
 */
export async function connectionState() {
  if (!supportsFileHandles()) return "unsupported";
  const handle = await storedHandle();
  if (!handle) return "not-connected";
  try {
    const state = await handle.queryPermission({ mode: "readwrite" });
    return state === "granted" ? "ready" : "needs-permission";
  } catch {
    return "needs-permission";
  }
}

/* ---------------- write ---------------- */

/**
 * Write the vault file, using a handle when we have one.
 *
 * @param {Uint8Array|string} contents ciphertext bundle
 * @param {{filename?:string, reuseHandle?:boolean}} [opts]
 * @returns {Promise<{transport:"handle"|"download", name:string}>}
 */
export async function writeVaultFile(contents, { filename = "tide-vault.json", reuseHandle = true, silent = false } = {}) {
  const data = typeof contents === "string" ? contents : new TextDecoder().decode(contents);

  if (supportsFileHandles()) {
    let handle = reuseHandle ? await storedHandle() : null;

    if (handle && !(await ensurePermission(handle))) handle = null;
    if (!handle && silent) {
      // A silent save must never summon a file picker. Callers that want the
      // user asked do it from a click, with silent off.
      throw new Error("No connected vault file");
    }
    if (!handle) {
      handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: "Tide vault", accept: { "application/json": [".json"] } }],
      });
      await rememberHandle(handle);
    }

    const writable = await handle.createWritable();
    await writable.write(data);
    await writable.close();
    return { transport: "handle", name: handle.name || filename };
  }

  // No pickers: hand the file to the platform's download/share path and let the
  // user put it wherever they keep things. Just as durable, one more tap.
  const url = URL.createObjectURL(new Blob([data], { type: "application/json" }));
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoked on a turn of the loop so the download has actually started.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return { transport: "download", name: filename };
}

/* ---------------- read ---------------- */

/**
 * Read the vault file back.
 *
 * After a wipe the handle is gone, so this asks the user to point at the file -
 * which is the whole recovery step: one click, not a re-pairing.
 *
 * @returns {Promise<{text:string, transport:"handle"|"picker", name:string}|null>}
 */
export async function readVaultFile({ reuseHandle = true } = {}) {
  if (supportsFileHandles()) {
    let handle = reuseHandle ? await storedHandle() : null;

    if (handle && !(await ensurePermission(handle, "read"))) handle = null;
    if (!handle) {
      const [picked] = await window.showOpenFilePicker({
        multiple: false,
        types: [{ description: "Tide vault", accept: { "application/json": [".json"] } }],
      });
      handle = picked;
      await rememberHandle(handle);
    }

    const file = await handle.getFile();
    return { text: await file.text(), transport: "handle", name: handle.name };
  }

  const file = await pickFile();
  if (!file) return null;
  return { text: await file.text(), transport: "picker", name: file.name };
}

/** <input type="file"> as a promise. Resolves null if the user cancels. */
function pickFile() {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.style.display = "none";
    // Cancel fires no event in every browser, so the input is cleaned up on
    // either signal and the promise resolves null rather than hanging.
    input.addEventListener("change", () => {
      const file = input.files?.[0] || null;
      input.remove();
      resolve(file);
    }, { once: true });
    input.addEventListener("cancel", () => {
      input.remove();
      resolve(null);
    }, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}
